import { ButtonIcon, Card, Dialog, Table, useDialog } from "@fpsoftware/mvp";
import { Instant } from "@js-joda/core";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { uniq } from "lodash-es";
import { type ChangeEvent, useMemo, useState } from "react";
import { TbPlus, TbTag, TbTrash } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import {
  type Address,
  type Borrower,
  formatAddress,
  formatInstantDate,
  formatMoney,
  type Purchase,
  RFQ_STATUS_LABELS,
  RFQ_STATUSES,
  type RfqStatus,
} from "shared";
import { useAddressesSuspense } from "@/addresses/hooks";
import { useBorrowersSuspense } from "@/borrower/hooks";
import { NoContent } from "@/common/NoContent";
import { Button } from "@/components/Button";
import { Input } from "@/components/form/Input";
import { Container } from "@/components/grid/Container";
import { Grid } from "@/components/grid/Grid";
import { Pagination } from "@/components/Pagination";
import { LinkGen } from "@/LinkGen";
import { AppPage } from "@/layout/AppLayout";
import { ChangePurchaseStatusDialog } from "@/purchases/ChangePurchaseStatusDialog";
import {
  usePurchaseActions,
  usePurchaseList,
  usePurchasesSuspense,
} from "@/purchases/hooks";
import styles from "./PageListPurchases.module.css";

const columnHelper = createColumnHelper<PurchaseRow>();

const columns = [
  columnHelper.accessor((row) => row.purchase.ID, {
    id: "ID",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor(
    (row) => `${row.borrower.firstName} ${row.borrower.lastName}`,
    {
      id: "Borrower",
      cell: (info) => info.getValue(),
    },
  ),
  columnHelper.accessor(
    (row) =>
      row.address === null
        ? "TBD"
        : formatAddress([
            row.address.line1,
            row.address.line2,
            row.address.city,
            row.address.state,
            row.address.zip_code,
          ]),
    {
      id: "Address",
      cell: (info) => info.getValue(),
    },
  ),
  columnHelper.accessor((row) => row.purchase.mortgageType, {
    id: "mortgageType",
    header: "Type",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row.purchase.price, {
    id: "price",
    header: "Price",
    meta: { align: "right" },
    cell: (info) => formatMoney(info.getValue()),
  }),
  columnHelper.accessor((row) => row.purchase.downpayment, {
    id: "downpayment",
    header: "Downpayment",
    meta: { align: "right" },
    cell: (info) => formatMoney(info.getValue()),
  }),
  columnHelper.accessor((row) => row.purchase.earnest, {
    id: "earnest",
    header: "Earnest",
    meta: { align: "right" },
    cell: (info) => formatMoney(info.getValue()),
  }),
  columnHelper.accessor((row) => row.purchase.status, {
    id: "status",
    header: "Status",
    cell: (info) => RFQ_STATUS_LABELS[info.getValue()],
  }),
  columnHelper.accessor(
    (row) => Instant.parse(row.purchase.updatedAt).toEpochMilli(),
    {
      id: "updatedAt",
      header: "Last Updated",
      cell: (info) => formatInstantDate(Instant.ofEpochMilli(info.getValue())),
    },
  ),
  columnHelper.display({
    meta: { align: "right" },
    cell: (info) => {
      // biome-ignore lint/suspicious/noExplicitAny: Easier way for local scoping
      const meta = info.table.options.meta as any;

      return (
        <div className={styles.actions}>
          <ButtonIcon
            icon={TbTag}
            onClick={(e) => {
              e.stopPropagation();
              meta?.onChangeStatus(info.row.original);
            }}
          />
          <ButtonIcon
            icon={TbTrash}
            onClick={(e) => {
              e.stopPropagation();
              meta?.onDelete(info.row.original);
            }}
          />
        </div>
      );
    },
    id: "actions",
  }),
];

interface PurchaseRow {
  purchase: Purchase;
  borrower: Borrower;
  address: Address | null;
}

const usePurchaseListTableData = () => {
  const purchaseIDs = usePurchaseList();
  const purchases = usePurchasesSuspense(purchaseIDs);

  const borrowerIDs = useMemo(
    () => uniq(purchases.map((x) => x.clientID)),
    [purchases],
  );
  const borrowers = useBorrowersSuspense(borrowerIDs);

  const addressIDs = useMemo(
    () =>
      uniq(
        purchases
          .map((x) => x.addressID)
          .filter((id): id is number => id !== null),
      ),
    [purchases],
  );
  const addresses = useAddressesSuspense(addressIDs);

  return useMemo<PurchaseRow[]>(() => {
    return purchases.map((purchase) => {
      const address =
        purchase.addressID === null
          ? null
          : (addresses.find((x) => x.ID === purchase.addressID) ?? null);

      if (purchase.addressID !== null && address === null) {
        throw new Error(`Unknown address for purchase ${purchase.ID}`);
      }

      const borrower = borrowers.find((x) => x.ID === purchase.clientID);

      if (!borrower) {
        throw new Error(`Unknown borrower for purchase ${purchase.ID}`);
      }

      return {
        purchase,
        borrower,
        address,
      };
    });
  }, [purchases, addresses, borrowers]);
};

export const PageListPurchases = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [activeStatuses, setActiveStatuses] = useState<Set<RfqStatus>>(
    new Set(),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const allTableData = usePurchaseListTableData();

  const tableData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return allTableData.filter((row) => {
      if (activeStatuses.size > 0 && !activeStatuses.has(row.purchase.status)) {
        return false;
      }
      if (query) {
        const borrowerName =
          `${row.borrower.firstName} ${row.borrower.lastName}`.toLowerCase();
        const address =
          row.address === null
            ? "tbd"
            : formatAddress([
                row.address.line1,
                row.address.line2,
                row.address.city,
                row.address.state,
                row.address.zip_code,
              ]).toLowerCase();
        if (!borrowerName.includes(query) && !address.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [allTableData, activeStatuses, searchQuery]);

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const toggleStatus = (status: RfqStatus) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const navigate = useNavigate();
  const dialog = useDialog();
  const { delete: deletePurchase } = usePurchaseActions();

  const onChangeStatus = (row: PurchaseRow) => {
    dialog.open(ChangePurchaseStatusDialog, { purchaseID: row.purchase.ID });
  };

  const onDelete = (row: PurchaseRow) => {
    dialog.open(Dialog.Confirm, {
      onConfirm: () => deletePurchase(row.purchase.ID),
      variant: "danger",
      confirmLabel: "Delete",
      confirmIcon: TbTrash,
      title: "Are you sure you want to delete this purchase?",
      children: (
        <>
          Borrower:{" "}
          <strong>
            {row.borrower.firstName} {row.borrower.lastName}
          </strong>
          <br />
          <br />
          Address:{" "}
          <strong>
            {row.address === null
              ? "TBD"
              : formatAddress([
                  row.address.line1,
                  row.address.line2,
                  row.address.city,
                  row.address.state,
                  row.address.zip_code,
                ])}
          </strong>
          <br />
          <br />
          Price: <strong>{formatMoney(row.purchase.price)}</strong>
          <br />
          <br />
          <i>
            <strong>This action is irreversible.</strong>
          </i>
        </>
      ),
    });
  };

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    meta: {
      onChangeStatus,
      onDelete,
    },
  });

  return (
    <AppPage title="Purchases">
      <Container>
        <Grid className="mb-3">
          <Grid.Col xs={3}>
            <Card>
              <Card.Header>Filters</Card.Header>
              <Card.Body>
                <Input
                  placeholder="Search by name or address…"
                  value={searchQuery}
                  onChange={onSearch}
                />
              </Card.Body>
              <Card.Body>
                <p className={styles.filterSectionTitle}>Status</p>
                {RFQ_STATUSES.map((status) => (
                  <label key={status} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={activeStatuses.has(status)}
                      onChange={() => toggleStatus(status)}
                    />
                    {RFQ_STATUS_LABELS[status]}
                  </label>
                ))}
              </Card.Body>
            </Card>
          </Grid.Col>
          <Grid.Col xs={9}>
            <Card>
              <Card.Header>
                Purchases
                <Button
                  size="sm"
                  icon={TbPlus}
                  onClick={() => navigate(LinkGen.Purchase.New())}
                >
                  Add purchase
                </Button>
              </Card.Header>
              <Table hover>
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeaderCell
                          alignRight={
                            // biome-ignore lint/suspicious/noExplicitAny: Easy fix
                            (header.column.columnDef.meta as any)?.align ===
                            "right"
                          }
                          sort={
                            header.column.getCanSort()
                              ? header.column.getIsSorted()
                              : undefined
                          }
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Table.HeaderCell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Head>
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row
                      key={row.id}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(
                          LinkGen.Purchase.Detail(row.original.purchase.ID),
                        )
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          alignRight={
                            // biome-ignore lint/suspicious/noExplicitAny: Easy fix
                            (cell.column.columnDef.meta as any)?.align ===
                            "right"
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                  {tableData.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan={10}>
                        <NoContent
                          label="No purchases found"
                          sublabel="Purchases will appear here once created"
                          image="/select-option.svg"
                        />
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              {table.getPageCount() > 1 && (
                <Pagination
                  pageIndex={table.getState().pagination.pageIndex}
                  pageCount={table.getPageCount()}
                  canPreviousPage={table.getCanPreviousPage()}
                  canNextPage={table.getCanNextPage()}
                  onPreviousPage={table.previousPage}
                  onNextPage={table.nextPage}
                />
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Container>
    </AppPage>
  );
};
